import Box from '@/common/components/Box'
import ButtonLink from '@/common/components/ButtonLink';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton';
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import SelectController from '@/common/components/SelectController'
import Table from '@/common/components/Table';
import TextField from '@/common/components/TextField';
import { useCurrentUser } from '@/common/hooks/useCurrentUser'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { MessageData } from '@/common/types/support/message.type'
import { UserData } from '@/common/types/users'
import { cn } from '@/common/utils/cn'
import { FormTransformers } from '@/common/utils/formTransformers'
import { FormMessage } from '@/features/support/external/components/FormMessage'
import { Message } from '@/features/support/external/components/Message'
import { strategy as statusStrategy } from '@/features/support/external/constants/statusOptions.constant'
import { useSupportTicket } from '@/features/support/external/hooks/useSupportTicket'
import { useSupportTicketMessages } from '@/features/support/external/hooks/useSupportTicketMessages'
import { useUpdateExternalSupportTicketResponsible } from '@/features/support/external/hooks/useUpdateExternalSupportTicketResponsible'
import {
  externalSupportTicketResponsibleSchema,
  ExternalSupportTicketResponsibleValidationSchema,
} from '@/features/support/external/schemas/create.schema'
import { supportTicketColumns } from '@/features/support/internal/constants/supportTicketColumns.constant';
import { InternalSupportTicketsTransformer } from '@/features/support/internal/services/internalSupportTicketsTransformer';
import { useUsers } from '@/features/users/hooks/useUsers'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { BriefcaseBusinessIcon, ChevronRightIcon, UserRoundIcon } from 'lucide-react'
import { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import ClosureData from '../../components/ClosureData';
import LastViewedInfo from '../../components/LastViewedData';

const ExternalSupportShow = () => {
  const { id: supportTicketId } = useParams()
  const queryClient = useQueryClient()

  const [searchUser, setSearchUser] = useDebounce('')

  const { data: user } = useCurrentUser({})

  const {
    data: supportTicketData,
    isFetching: supportTicketDataIsFetching,
    isPending: supportTicketDataIsPending,
    error: supportTicketDataError = {} as any,
    refetch: refetchSupportTicket,
  } = useSupportTicket({
    id: supportTicketId || '',
  })

  const { data: supportTicketMessagesData } = useSupportTicketMessages<MessageData[]>({
    id: supportTicketId || '',
  })

  const { data: users } = useUsers({
    filters: {
      search: searchUser,
    },
    mapper: Mapper.mapToOptions<UserData>({ labelFieldName: 'full_name', valueFieldName: 'id' }),
  })

  const { mutate: updateSupportTicketResponsible, isPending: updateSupportTicketResponsibleIsPending } =
    useUpdateExternalSupportTicketResponsible({
      supportTicketId,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['supportTicket', supportTicketId] })
      },
      onError: () => {
        FormTransformers.errorsTransformer<ExternalSupportTicketResponsibleValidationSchema>({
          setError,
        })
      },
    })

  const { control, handleSubmit, setError, reset } = useForm<ExternalSupportTicketResponsibleValidationSchema>({
    resolver: zodResolver(externalSupportTicketResponsibleSchema),
  })

  const onSubmit: SubmitHandler<ExternalSupportTicketResponsibleValidationSchema> = (data) =>
    updateSupportTicketResponsible(data)

  const submit = handleSubmit(onSubmit)

  useEffect(() => {
    if (!supportTicketDataIsFetching) {
      reset({
        responsible: supportTicketData?.responsible
          ? { label: supportTicketData?.responsible.full_name, value: supportTicketData?.responsible.id }
          : undefined,
      })
    }
  }, [supportTicketDataIsFetching])

  const isFromAdmin = !supportTicketData?.creator?.branch && !supportTicketData?.creator?.company;

  const companyId = supportTicketData?.branch?.company?.id

  const linkByCompanyType =
    (isFromAdmin && supportTicketData?.branch?.company?.kind) ||
    (!isFromAdmin && supportTicketData?.creator?.branch)
      ? `/companies/${companyId}`
      : `/establishments/${companyId}`;

  const creatorButtonPermissionKlass = isFromAdmin ? 'Admin::Users::Show' : supportTicketData?.branch?.company?.kind ? 'Admin::Client::Users::Show' : 'Admin::Establishment::Users::Show'

  const companyButtonPermissionKlass = supportTicketData?.branch?.company?.kind ? 'Admin::Client::Companies::Show' : 'Admin::Establishment::Companies::Show'

  const showClosureData = !!supportTicketData && supportTicketData?.status === 'closed' && supportTicketData.closed_at
  const showLastViewedData = !!supportTicketData && supportTicketData.company_last_viewed_by

  return (
    <>
      {supportTicketDataIsPending ? (
        <Box>Carregando...</Box>
      ) : (
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-128px)]"
          statusCode={supportTicketDataError?.status}
          refetchFunction={refetchSupportTicket}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-4 items-center">
              <GoBackButton
                fallback='/external-support'
                searchLastRouteFromReference={`/external-support/${supportTicketId}`}
              />

              <div className="flex gap-2 items-center">
                <h2 className="text-2xl font-bold">{supportTicketData?.reference_number}</h2>

                <div
                  data-testid="status_badge"
                  className={cn(
                    'px-2 py-1 rounded-sm',
                    supportTicketData?.status &&
                    ['pending', 'awaiting_tcard_response'].includes(supportTicketData?.status) &&
                    'bg-yellow-op-2',
                    supportTicketData?.status &&
                    ['awaiting_customer_response', 'closed'].includes(supportTicketData?.status) &&
                    'bg-blue-200',
                  )}
                >
                  {statusStrategy[supportTicketData?.status as keyof typeof statusStrategy]}
                </div>
              </div>
            </div>
          </div>

          <Box className="flex flex-col gap-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-128px)]">
            <div className="flex flex-col gap-4">
              <SelectController
                control={control}
                name="responsible"
                label="Responsável"
                triggerOnChange={submit}
                options={users}
                onInputChange={(value) => setSearchUser(value)}
                isDisabled={supportTicketData?.status === 'closed' || updateSupportTicketResponsibleIsPending}
              />

              <p className="text-lg font-bold">Autor</p>

              <div className="flex gap-2 items-center">
                <ButtonLink
                  linkTo={isFromAdmin ? `/users/${supportTicketData?.creator.id}` : `${linkByCompanyType}/users/${supportTicketData?.creator.id}`}
                  operationKlassToCheckPermission={creatorButtonPermissionKlass}
                  dataTestId="creator_clickable_link"
                >
                  <ImageWithPlaceholder
                    url={supportTicketData?.creator?.profile_picture?.url}
                    alt={supportTicketData?.creator?.full_name}
                    placeholderIcon={UserRoundIcon}
                    className="size-8"
                    iconClassName="size-6"
                  />

                  <div className='flex items-center gap-1'>
                    <p className="text-sm font-bold">{supportTicketData?.creator?.full_name}</p>

                    {isFromAdmin && (
                      <p className='text-sm font-light italic'>(Agente Tcard)</p>
                    )}
                  </div>
                </ButtonLink>


                {isFromAdmin ? (
                  <ButtonLink
                    linkTo={linkByCompanyType}
                    operationKlassToCheckPermission={companyButtonPermissionKlass}
                    dataTestId="company_clickable_link"
                  >
                    <ChevronRightIcon strokeWidth={1.5} size={16} />

                    <ImageWithPlaceholder
                      url={
                        supportTicketData?.branch?.company?.general_information?.company_logo?.url
                      }
                      placeholderIcon={BriefcaseBusinessIcon}
                      className="size-8"
                      iconClassName="size-6"
                    />

                    <div>
                      <p className="text-sm font-bold">
                        {supportTicketData?.branch?.company?.general_information?.fantasy_name}
                      </p>

                      {!!supportTicketData?.branch?.display_name && (
                        <p className="text-sm font-light">
                          {supportTicketData?.branch?.display_name}
                        </p>
                      )}
                    </div>
                  </ButtonLink>
                ) : (
                  <>
                    <p className="text-sm font-light italic">de</p>

                    <ButtonLink
                      linkTo={linkByCompanyType}
                      operationKlassToCheckPermission={companyButtonPermissionKlass}
                      dataTestId="company_clickable_link"
                    >
                      <ImageWithPlaceholder
                        url={
                          supportTicketData?.creator?.branch?.general_information?.company_logo?.url ||
                          supportTicketData?.creator?.company?.general_information?.company_logo?.url
                        }
                        alt={
                          supportTicketData?.creator?.branch?.display_name ||
                          supportTicketData?.creator?.company?.general_information?.fantasy_name
                        }
                        placeholderIcon={BriefcaseBusinessIcon}
                        className="size-8"
                        iconClassName="size-6"
                      />

                      <p className="text-sm font-bold">
                        {supportTicketData?.creator?.company?.general_information?.fantasy_name}{' '}
                        {!!supportTicketData?.creator?.branch?.display_name &&
                          `(${supportTicketData?.creator?.branch?.display_name})`}
                      </p>
                    </ButtonLink>
                  </>
                )}
              </div>

              {Number(supportTicketData?.tickets_that_links?.length) > 0 && (
                <div className="flex w-full flex-col gap-y-4">
                  <p className="text-lg font-bold">Chamado vinculados</p>

                  <Table
                    className={cn('max-h-[14.75rem]', {
                      'min-h-14':
                        supportTicketData?.tickets_that_links && supportTicketData?.tickets_that_links?.length > 0,
                    })}
                    columns={supportTicketColumns}
                    rows={InternalSupportTicketsTransformer.supportTicketsTableTransformer()({
                      supportTickets: supportTicketData?.tickets_that_links || [],
                      actionType: 'link',
                    })}
                    showEmptyTable
                    rowEmptyValue="--"
                    page={1}
                    totalPages={1}
                    isLoadingData={false}
                  />
                </div>
              )}

              <TextField
                label="Assunto"
                value={supportTicketData?.subject_line}
                valueClassName="text-white-800"
                disabled
              />
            </div>

            {supportTicketMessagesData?.map((message) => (
              <Message key={message.id} message={message} margin={message.creator.id === user?.id ? 'left' : 'right'} />
            ))}

            {!!supportTicketData && supportTicketData?.status !== 'closed' && (
              <FormMessage supportTicketId={supportTicketData?.id} />
            )}

            {showClosureData && (
              <ClosureData supportTicket={supportTicketData} />
            )}

            {showLastViewedData && (
              <LastViewedInfo supportTicket={supportTicketData} />
            )}
          </Box>
        </ErrorMessageWrapper>
      )}
    </>
  )
}

export default ExternalSupportShow
